import httpClient from './httpClient';

export interface StandaloneLessonData {
  title: string;
  lessonType: 'ILTS' | 'Content-Based';
  contentType?: 'document' | 'scorm' | 'mp4' | 'url';
  lessonContentDocument?: string | File | null;
  scormPackage?: string | File | null;
  videoUpload?: string | File | null;
  contentUrl?: string;
  lessonDuration?: string;
  duration?: string;
  description?: string;
  iltsType?: 'Online' | 'Offline';
  meetingUrl?: string;
  meetUrl?: string;
  eventVenue?: string;
  venue?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
}

export interface StandaloneLessonResponse {
  id: number;
  title: string;
  lessonType: 'ILTS' | 'Content-Based';
  contentType?: string | null;
  lessonContentDocument?: string | null;
  lessonContentScorm?: string | null;
  lessonContentMp4?: string | null;
  lessonContentUrl?: string | null;
  lessonDuration?: string | null;
  description?: string | null;
  createdDate: string;
  lastUpdated: string;
  creatorId: string;
  creatorName?: string | null;
  iltsInfo?: {
    mode: 'Online' | 'Offline';
    meetUrl?: string | null;
    venue?: string | null;
    startDate?: string | null;
    startTime?: string | null;
    endDate?: string | null;
    endTime?: string | null;
  } | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: {
    message: string;
  };
}

/**
 * Create a standalone lesson (not associated with any course)
 */
export const createStandaloneLesson = async (
  lessonData: StandaloneLessonData
): Promise<ApiResponse<StandaloneLessonResponse>> => {
  try {
    const response = await httpClient.post<ApiResponse<StandaloneLessonResponse>>(
      '/super-admin/content/lessons',
      lessonData
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create standalone lesson',
      },
    };
  }
};

/**
 * Get all standalone lessons
 */
export const getStandaloneLessons = async (): Promise<ApiResponse<StandaloneLessonResponse[]>> => {
  try {
    const response = await httpClient.get<ApiResponse<StandaloneLessonResponse[]>>(
      '/super-admin/content/lessons'
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch standalone lessons',
      },
    };
  }
};

/**
 * Get a specific standalone lesson by ID
 */
export const getStandaloneLessonById = async (
  lessonId: number
): Promise<ApiResponse<StandaloneLessonResponse>> => {
  try {
    const response = await httpClient.get<ApiResponse<StandaloneLessonResponse>>(
      `/super-admin/content/lessons/${lessonId}`
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch standalone lesson',
      },
    };
  }
};

/**
 * Update a standalone lesson
 */
export const updateStandaloneLesson = async (
  lessonId: number,
  lessonData: StandaloneLessonData
): Promise<ApiResponse<{ success: boolean }>> => {
  try {
    const response = await httpClient.put<ApiResponse<{ success: boolean }>>( `/super-admin/content/lessons/${lessonId}`,
      lessonData
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update standalone lesson',
      },
    };
  }
};

/**
 * Delete a standalone lesson
 */
export const deleteStandaloneLesson = async (
  lessonId: number
): Promise<ApiResponse<{ success: boolean }>> => {
  try {
    const response = await httpClient.delete<ApiResponse<{ success: boolean }>>(
      `/super-admin/content/lessons/${lessonId}`
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete standalone lesson',
      },
    };
  }
};
